import React, { useEffect, useCallback } from "react";

// Format city slug for display (e.g. "delhi-ncr" -> "Delhi-NCR")
const formatCityDisplay = (slug) => {
  if (!slug) return "";
  if (slug.toLowerCase() === "delhi-ncr") return "Delhi-NCR";
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const PricingSelectCityModal = ({
  isOpen,
  onClose,
  currentCitySlug,
  availableCities = [],
  onSelectCity,
}) => {
  const handleEscape = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleCityClick = (slug) => {
    if (slug === currentCitySlug) {
      onClose();
      return;
    }
    onSelectCity(slug);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="select-city-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      {/* Backdrop - tap to close */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200"
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-md max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between shrink-0 px-4 md:px-6 py-4 border-b border-gray-100">
          <h2
            id="select-city-title"
            className="text-lg md:text-xl font-semibold text-gray-900"
          >
            Select your city
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -m-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#024670] focus:ring-offset-2 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* City list - scrollable on small screens */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 md:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
            {availableCities.map((slug) => {
              const isCurrent = slug === currentCitySlug;
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => handleCityClick(slug)}
                  className={`
                    px-4 py-3 min-h-[48px] rounded-xl text-left text-sm md:text-base font-medium
                    transition-all duration-150 touch-manipulation
                    focus:outline-none focus:ring-2 focus:ring-[#024670] focus:ring-offset-2
                    ${isCurrent
                      ? "bg-[#024670] text-white shadow-md ring-2 ring-[#024670]/20"
                      : "bg-gray-50 text-gray-800 hover:bg-gray-100 active:bg-gray-200 border border-gray-100"
                    }
                  `}
                >
                  {formatCityDisplay(slug)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSelectCityModal;
